import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useUpdateStream, type DrmStream, type UpdateInput } from '@/api/aes';
import { trackFeatureAction } from '@/lib/analytics-events';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stream: DrmStream;
}

/**
 * Edit a stream's key policy — the LIVE rotation period and the DVR window —
 * via PUT /streams/{app}/{name} (owner-gated; the broadcast address is
 * immutable, so only these knobs are editable here). The rotation period is
 * LIVE-only; VOD streams just edit the DVR window. On save the stream query is
 * invalidated, so the detail page's Metrics + Key rotation views re-read the new
 * values. Mirrors the key-policy step of the provision wizard.
 */
export function StreamConfigDialog({ open, onOpenChange, stream }: Props) {
  const { show } = useToast();
  const update = useUpdateStream();
  const isLive = stream.kind === 'live';

  const [periodSecs, setPeriodSecs] = useState(stream.period_secs);
  const [dvrSeconds, setDvrSeconds] = useState(stream.dvr_seconds);
  const [error, setError] = useState<string | null>(null);

  // Re-seed from the current stream each time the dialog opens (the values may
  // have changed since mount via a prior save or a refetch).
  useEffect(() => {
    if (open) {
      setPeriodSecs(stream.period_secs);
      setDvrSeconds(stream.dvr_seconds);
      setError(null);
    }
  }, [open, stream.period_secs, stream.dvr_seconds]);

  function handleSubmit() {
    if (isLive && periodSecs < 1) {
      setError('Rotation period must be at least 1 second.');
      return;
    }
    if (dvrSeconds < 0) {
      setError('DVR window cannot be negative.');
      return;
    }
    setError(null);

    const patch: UpdateInput = { dvr_seconds: dvrSeconds };
    if (isLive) patch.period_secs = periodSecs;

    update.mutate(
      { id: stream.id, patch },
      {
        onSuccess: () => {
          show({ tone: 'success', title: `Updated "${stream.id}" key policy` });
          trackFeatureAction('aes', 'stream', 'edit');
          onOpenChange(false);
        },
        onError: (e) =>
          show({ tone: 'danger', title: (e as Error)?.message || "Couldn't update key policy" }),
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Configure key policy</DialogTitle>
          <DialogDescription>
            Key rotation and DVR window for <strong>{stream.id}</strong>. The broadcast address
            can't change.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form
            id="stream-config-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
          >
            {isLive && (
              <Field
                label="Rotation period (seconds)"
                description="How often the AES-128 key rotates on the live edge."
                error={error && periodSecs < 1 ? error : undefined}
              >
                {(p) => (
                  <Input
                    {...p}
                    type="number"
                    min={1}
                    value={String(periodSecs)}
                    invalid={!!error && periodSecs < 1}
                    onChange={(e) => setPeriodSecs(Number(e.target.value) || 0)}
                  />
                )}
              </Field>
            )}
            <Field
              label="DVR window (seconds)"
              description="How far back viewers can seek. 0 disables timeshift."
              error={error && dvrSeconds < 0 ? error : undefined}
            >
              {(p) => (
                <Input
                  {...p}
                  type="number"
                  min={0}
                  value={String(dvrSeconds)}
                  invalid={!!error && dvrSeconds < 0}
                  onChange={(e) => setDvrSeconds(Number(e.target.value) || 0)}
                />
              )}
            </Field>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="stream-config-form" disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default StreamConfigDialog;
